import i18n from 'i18next';
import moment, { Moment } from 'moment';
import { all, call, put, select, takeLeading } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import limitNameForAuth from '@app/libs/limitNameForAuth';
import { requestCertification } from '@app/libs/portone/certification';
import workspaceService from '@app/libs/workspaceService';
import {
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_REQUEST,
  CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_REQUEST,
  CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_REQUEST,
  CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_REQUEST,
  CONTRACT_AUTHENTICATION_PHONE_RETRY_REQUEST,
} from '@app/store/actionTypes';
import {
  contractAuthenticationDigitalCertificateGuideCheck,
  contractAuthenticationDigitalCertificateGuideCheckFailure,
  contractAuthenticationDigitalCertificateGuideCheckSuccess,
  contractAuthenticationPasswordAuthenticateFailure,
  contractAuthenticationPasswordAuthenticateRequest,
  contractAuthenticationPasswordAuthenticateSuccess,
  contractAuthenticationPhoneCertificateFailure,
  contractAuthenticationPhoneCertificateRequest,
  contractAuthenticationPhoneCertificateSuccess,
  contractAuthenticationPhoneCompleteFailure,
  contractAuthenticationPhoneCompleteSuccess,
  contractAuthenticationPhoneRetryFailure,
  contractAuthenticationPhoneRetryRequest,
  contractAuthenticationPhoneRetrySuccess,
  fetchAuthenticationFailureStatus,
  fetchAuthenticationFailureStatusFailure,
  fetchAuthenticationFailureStatusSuccess,
} from '@app/store/contract-authentication/actions';
import AuthenticationGuideCheckStatus from '@app/store/contract-authentication/AuthenticationGuideCheckStatus';
import ContractAuthenticationStatus from '@app/store/contract-authentication/ContractAuthenticationStatus';
import {
  getAuthenticationKey,
  getDigitalCertificateAuthentication,
  getDocumentId,
  getParticipantId,
  getPhoneAuthentication,
} from '@app/store/contract-authentication/selectors';
import { modalOpen } from '@app/store/modal/actions';
import { getRequesterName } from '@app/store/participant/selectors';
import { checkDocumentsParticipantsAuthsAuthenticationStatusByType } from '@app/store/transport-layer/document/documents/participants/auths';
import { postDocumentsParticipantsAuthsAttempt } from '@app/store/transport-layer/document/documents/participants/auths/attempt';
import {
  DocumentMobileIdentificationResponse,
  postDocumentsParticipantsVerificationMobileIdentification,
} from '@app/store/transport-layer/document/documents/participants/verification/mobile-identification';
import { postDocumentsParticipantsAuthsGuideCheck } from '@app/store/transport-layer/document/documents/participants/verification/pre-verify';

import ContractAuthenticationDetailStatus from './ContractAuthenticationDetailStatus';

const AUTHENTICATION_EXCEEDED = 'authentication exceeded';

export const getRetryAtMoment = ({
  failureCount,
  lastFailedAt,
}: {
  failureCount: number;
  lastFailedAt?: string;
}): Moment | null => {
  return failureCount !== 0 && failureCount % 5 === 0 && lastFailedAt
    ? moment(lastFailedAt).add(10, 'm')
    : null;
};

export const getLeftFailureCount = (failureCount: number) => {
  return failureCount % 5 === 0 ? 0 : 5 - (failureCount % 5);
};

/**
 * 접근 암호 인증의 암호가 일치하는지 검증한다.
 */
export function* authenticatePassword({
  payload: { id, answer },
}: ReturnType<typeof contractAuthenticationPasswordAuthenticateRequest>) {
  const authenticationKey: ReturnType<typeof getAuthenticationKey> =
    yield select(getAuthenticationKey);
  const documentId: ReturnType<typeof getDocumentId> = yield select(getDocumentId);
  const participantId: ReturnType<typeof getParticipantId> = yield select(getParticipantId);

  try {
    if (authenticationKey && documentId && participantId) {
      const result: {
        authId: string;
        authKey: string;
        failureCount: number;
        lastFailedAt?: string;
        status: ContractAuthenticationStatus;
      } = yield call(postDocumentsParticipantsAuthsAttempt, {
        authenticationId: id,
        authenticationKey,
        authType: 'PASSWORD',
        authValue: answer,
        documentId,
        participantId,
        workspaceId: workspaceService.getWorkspaceId(),
      });
      const restCount = result.failureCount % 5 === 0 ? 0 : 5 - (result.failureCount % 5);

      if (
        result.status === ContractAuthenticationStatus.PENDING ||
        result.status === ContractAuthenticationStatus.SUCCEEDED
      ) {
        yield put(
          contractAuthenticationPasswordAuthenticateSuccess({
            failureCount: result.failureCount,
            lastFailedAt: result.lastFailedAt,
            retryAt: null,
            status: result.status,
          })
        );
      }

      if (
        result.status === ContractAuthenticationStatus.FAILED ||
        result.status === ContractAuthenticationStatus.EXCEEDED
      ) {
        yield put(
          contractAuthenticationPasswordAuthenticateFailure({
            failureInfo: {
              failureCount: result.failureCount,
              lastFailedAt: result.lastFailedAt,
              retryAt:
                result.status === ContractAuthenticationStatus.EXCEEDED
                  ? moment().add(10, 'm')
                  : null,
              status: result.status,
            },
          })
        );
        yield put(
          modalOpen('CONFIRM', {
            title: i18n.t('authenticatePassword-failedModalTitle'),
            content:
              result.status === ContractAuthenticationStatus.EXCEEDED
                ? i18n.t('authenticatePassword-tryBannedModalMessage')
                : i18n.t(
                    'authenticatePassword-failedModalMessage',
                    result.status === ContractAuthenticationStatus.FAILED
                      ? {
                          count: restCount,
                        }
                      : undefined
                  ),
          })
        );
      }
    }
  } catch (error) {
    yield put(
      contractAuthenticationPasswordAuthenticateFailure({
        error,
        failureInfo: {
          failureCount: 0,
          retryAt: moment().add(10, 'm'),
          status: ContractAuthenticationStatus.EXCEEDED,
        },
      })
    );
    const passwordResponses: {
      [id: string]: string;
    } = {
      [AUTHENTICATION_EXCEEDED]: i18n.t('authenticatePassword-exceededTriesModalMessage'),
      'invalid authentication key': i18n.t('authenticatePassword-invalidAuthenticationKey'),
    };

    yield put(
      modalOpen('CONFIRM', {
        title: i18n.t('authenticatePassword-failedModalTitle'),
        content:
          passwordResponses[error.message] || GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

/**
 * 휴대폰 본인 인증을 재시도한다.
 */
export function* authenticatePhoneCertificationRetry({
  payload: { id: authenticationId },
}: ReturnType<typeof contractAuthenticationPhoneRetryRequest>) {
  const authenticationKey: ReturnType<typeof getAuthenticationKey> =
    yield select(getAuthenticationKey);
  const mobileAuthentication: ReturnType<typeof getPhoneAuthentication> =
    yield select(getPhoneAuthentication);
  const documentId: ReturnType<typeof getDocumentId> = yield select(getDocumentId);
  const participantId: ReturnType<typeof getParticipantId> = yield select(getParticipantId);

  if (
    authenticationKey &&
    mobileAuthentication &&
    mobileAuthentication.phoneNumberLastDigits &&
    documentId &&
    participantId
  ) {
    const { phoneNumber, phoneNumberLastDigits, realName } = mobileAuthentication;

    try {
      const status: string = yield call(postDocumentsParticipantsAuthsGuideCheck, {
        workspaceId: workspaceService.getWorkspaceId(),
        answer: phoneNumberLastDigits,
        authGuide: `${limitNameForAuth(realName)}|${phoneNumber}`,
        authType: 'MOBILE_IDENTIFICATION',
        authenticationId,
        documentId,
        participantId,
      });
      const { failureCount, lastFailedAt }: { failureCount: number; lastFailedAt?: string } =
        yield call(checkDocumentsParticipantsAuthsAuthenticationStatusByType, {
          authenticationType: 'MOBILE_IDENTIFICATION',
          authenticationKey,
          documentId,
          participantId,
          workspaceId: workspaceService.getWorkspaceId(),
        });
      if (status === AuthenticationGuideCheckStatus.CORRECT) {
        yield put(contractAuthenticationPhoneRetrySuccess({ answer: phoneNumberLastDigits }));
        const iamportCertificationResponse: PortoneCertificationResponse = yield call(
          requestCertification,
          {
            name: realName,
            phone: phoneNumber + phoneNumberLastDigits,
          }
        );
        if (iamportCertificationResponse.success) {
          const authenticationResult: DocumentMobileIdentificationResponse = yield call(
            postDocumentsParticipantsVerificationMobileIdentification,
            {
              documentId,
              participantId,
              authenticationKey,
              impUid: iamportCertificationResponse.imp_uid,
              workspaceId: workspaceService.getWorkspaceId(),
            }
          );
          if (authenticationResult.status === 'SUCCEEDED') {
            yield put(
              contractAuthenticationPhoneCompleteSuccess({
                authenticationDetailStatus: ContractAuthenticationDetailStatus.COMPLETED,
                failureCount: authenticationResult.failureCount,
                retryAt: null,
                status: ContractAuthenticationStatus.SUCCEEDED,
              })
            );
          } else {
            yield put(
              contractAuthenticationPhoneCompleteFailure({
                failureInfo: {
                  failureCount: authenticationResult.failureCount,
                  lastFailedAt: authenticationResult.lastFailedAt,
                  leftFailureCount: getLeftFailureCount(authenticationResult.failureCount),
                  retryAt: getRetryAtMoment({
                    failureCount: authenticationResult.failureCount,
                    lastFailedAt: authenticationResult.lastFailedAt,
                  }),
                },
                authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILED,
              })
            );
          }
        } else {
          yield put(
            contractAuthenticationPhoneRetryFailure({
              failureInfo: {
                failureCount,
                lastFailedAt,
                leftFailureCount: getLeftFailureCount(failureCount),
                retryAt: getRetryAtMoment({ failureCount, lastFailedAt }),
              },
              authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILURE_CANCEL,
            })
          );
        }
      } else {
        yield put(
          contractAuthenticationPhoneCertificateFailure({
            failureInfo: {
              failureCount,
              lastFailedAt,
              leftFailureCount: getLeftFailureCount(failureCount),
              retryAt: getRetryAtMoment({ failureCount, lastFailedAt }),
            },
            authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILURE_GUIDE_CHECK,
          })
        );
      }
    } catch (error) {
      yield put(
        contractAuthenticationPhoneRetryFailure({
          error,
          authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILURE_SERVER,
        })
      );
    }
  }
}

/**
 * 휴대폰 본인 인증에서 뒷번호 4자리를 검증한다.
 * 아임포트 휴대폰 본인 인증 과정을 실행한다.
 */
export function* authenticatePhoneCertification({
  payload: { id: authenticationId, answer: phoneNumberLastDigits },
}: ReturnType<typeof contractAuthenticationPhoneCertificateRequest>) {
  const authenticationKey: ReturnType<typeof getAuthenticationKey> =
    yield select(getAuthenticationKey);

  const mobileAuthentication: ReturnType<typeof getPhoneAuthentication> =
    yield select(getPhoneAuthentication);
  const documentId: ReturnType<typeof getDocumentId> = yield select(getDocumentId);
  const participantId: ReturnType<typeof getParticipantId> = yield select(getParticipantId);

  if (
    authenticationKey &&
    mobileAuthentication &&
    phoneNumberLastDigits &&
    documentId &&
    participantId
  ) {
    const { phoneNumber, realName } = mobileAuthentication;
    try {
      const status: AuthenticationGuideCheckStatus = yield call(
        postDocumentsParticipantsAuthsGuideCheck,
        {
          workspaceId: workspaceService.getWorkspaceId(),
          answer: phoneNumberLastDigits,
          authGuide: `${limitNameForAuth(realName)}|${phoneNumber}`,
          authType: 'MOBILE_IDENTIFICATION',
          authenticationId,
          documentId,
          participantId,
        }
      );

      const { failureCount, lastFailedAt }: { failureCount: number; lastFailedAt?: string } =
        yield call(checkDocumentsParticipantsAuthsAuthenticationStatusByType, {
          authenticationType: 'MOBILE_IDENTIFICATION',
          authenticationKey,
          documentId,
          participantId,
          workspaceId: workspaceService.getWorkspaceId(),
        });

      if (status === AuthenticationGuideCheckStatus.CORRECT) {
        yield put(
          contractAuthenticationPhoneCertificateSuccess({
            answer: phoneNumberLastDigits,
          })
        );
        const iamportCertificationResponse: PortoneCertificationResponse = yield call(
          requestCertification,
          {
            name: realName,
            phone: phoneNumber + phoneNumberLastDigits,
          }
        );
        if (iamportCertificationResponse.success) {
          const authenticationResult: DocumentMobileIdentificationResponse = yield call(
            postDocumentsParticipantsVerificationMobileIdentification,
            {
              documentId,
              participantId,
              authenticationKey,
              impUid: iamportCertificationResponse.imp_uid,
              workspaceId: workspaceService.getWorkspaceId(),
            }
          );
          if (authenticationResult.status === 'SUCCEEDED') {
            yield put(
              contractAuthenticationPhoneCompleteSuccess({
                authenticationDetailStatus: ContractAuthenticationDetailStatus.COMPLETED,
                failureCount: authenticationResult.failureCount,
                retryAt: null,
                status: ContractAuthenticationStatus.SUCCEEDED,
              })
            );
          } else {
            yield put(
              contractAuthenticationPhoneCompleteFailure({
                failureInfo: {
                  failureCount: authenticationResult.failureCount,
                  lastFailedAt: authenticationResult.lastFailedAt,
                  leftFailureCount: getLeftFailureCount(authenticationResult.failureCount),
                  retryAt: getRetryAtMoment({
                    failureCount: authenticationResult.failureCount,
                    lastFailedAt: authenticationResult.lastFailedAt,
                  }),
                },
                authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILED,
              })
            );
          }
        } else {
          yield put(
            contractAuthenticationPhoneRetryFailure({
              failureInfo: {
                failureCount,
                lastFailedAt,
                leftFailureCount: getLeftFailureCount(failureCount),
                retryAt: getRetryAtMoment({ failureCount, lastFailedAt }),
              },
              authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILURE_CANCEL,
            })
          );
        }
      } else {
        yield put(
          contractAuthenticationPhoneCertificateFailure({
            failureInfo: {
              failureCount,
              lastFailedAt,
              leftFailureCount: getLeftFailureCount(failureCount),
              retryAt: getRetryAtMoment({ failureCount, lastFailedAt }),
            },
            authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILURE_GUIDE_CHECK,
          })
        );
      }
    } catch (error) {
      yield put(
        contractAuthenticationPhoneCompleteFailure({
          error,
          authenticationDetailStatus: ContractAuthenticationDetailStatus.FAILURE_SERVER,
        })
      );
    }
  }
}

/**
 * 법인 공동 인증서 실패 수 실패 정보를 조회합니다.
 */
export function* fetchAuthenticationFailureStatusSaga({
  payload: { authenticationKey, documentId, participantId },
}: ReturnType<typeof fetchAuthenticationFailureStatus>) {
  try {
    const {
      authType,
      failureCount,
      lastFailedAt,
    }: {
      authType: 'MOBILE_IDENTIFICATION' | 'D_CERT';
      failureCount: number;
      lastFailedAt?: string;
    } = yield call(checkDocumentsParticipantsAuthsAuthenticationStatusByType, {
      authenticationType: 'D_CERT',
      authenticationKey,
      documentId,
      participantId,
      workspaceId: workspaceService.getWorkspaceId(),
    });

    yield put(
      fetchAuthenticationFailureStatusSuccess({
        authType,
        failureCount,
        lastFailedAt,
        retryAt: getRetryAtMoment({ failureCount, lastFailedAt }),
      })
    );
  } catch (_error) {
    yield put(fetchAuthenticationFailureStatusFailure());
  }
}

/**
 * 법인 공동 인증서의 사업자번호 뒤 5자리를 검증한다.
 */
export function* authenticateDigitalCertificateBizNumber({
  payload: { authenticationId, answer },
}: ReturnType<typeof contractAuthenticationDigitalCertificateGuideCheck>) {
  const authenticationKey: ReturnType<typeof getAuthenticationKey> =
    yield select(getAuthenticationKey);
  const digitalCertificateAuth: ReturnType<typeof getDigitalCertificateAuthentication> =
    yield select(getDigitalCertificateAuthentication);
  const documentId: ReturnType<typeof getDocumentId> = yield select(getDocumentId);
  const participantId: ReturnType<typeof getParticipantId> = yield select(getParticipantId);
  const requesterName: ReturnType<typeof getRequesterName> = yield select(getRequesterName);

  try {
    if (authenticationKey && digitalCertificateAuth && documentId && participantId) {
      const { bizNumber, companyName } = digitalCertificateAuth;
      const status: AuthenticationGuideCheckStatus = yield call(
        postDocumentsParticipantsAuthsGuideCheck,
        {
          workspaceId: workspaceService.getWorkspaceId(),
          answer,
          authGuide: `${companyName}|${bizNumber}`,
          authType: 'D_CERT',
          authenticationId,
          documentId,
          participantId,
        }
      );

      const { failureCount, lastFailedAt }: { failureCount: number; lastFailedAt?: string } =
        yield call(checkDocumentsParticipantsAuthsAuthenticationStatusByType, {
          authenticationType: 'D_CERT',
          authenticationKey,
          documentId,
          participantId,
          workspaceId: workspaceService.getWorkspaceId(),
        });

      if (status === AuthenticationGuideCheckStatus.CORRECT) {
        yield put(contractAuthenticationDigitalCertificateGuideCheckSuccess({ answer }));
      } else {
        yield put(modalOpen('DIGITAL_CERTIFICATE_GUIDE_CHECK_ERROR_MODAL', { requesterName }));
        yield put(
          contractAuthenticationDigitalCertificateGuideCheckFailure({
            failureInfo: {
              failureCount,
              lastFailedAt,
              retryAt: getRetryAtMoment({ failureCount, lastFailedAt }),
            },
          })
        );
      }
    }
  } catch (error) {
    yield put(contractAuthenticationDigitalCertificateGuideCheckFailure({ error }));
    yield put(
      modalOpen('CONFIRM', {
        title: '인증 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export default function* contractAuthenticationSagas() {
  yield all([
    takeLeading(
      CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_REQUEST,
      authenticateDigitalCertificateBizNumber
    ),
    takeLeading(
      CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_REQUEST,
      fetchAuthenticationFailureStatusSaga
    ),
    takeLeading(CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_REQUEST, authenticatePassword),
    takeLeading(CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_REQUEST, authenticatePhoneCertification),
    takeLeading(CONTRACT_AUTHENTICATION_PHONE_RETRY_REQUEST, authenticatePhoneCertificationRetry),
  ]);
}
